import React, { Component, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AppNavbar from './components/AppNavbar';
import { connect } from 'react-redux';
import { loadUser } from './actions/authActions';
import { checkOnboardingStatus } from './actions/onboardingActions';

import ChatBot from './components/chatbot/ChatBot';
import './App.css';
import Spinner from './components/common/Spinner';

//Pages
import Home from './pages/Home';
import Settings from './pages/Settings';
import Profile from './components/profile/Profile';
import Dashboard from './pages/Dashboard';
import Messages from './pages/Messages';
import ContentManagement from './pages/ContentManagement';
import LessonsManagement from './pages/LessonsManagement';
import CoursesManagement from './pages/CoursesManagement';
import SubmissionsManagement from './pages/SubmissionsManagement';
import PathsManagement from './pages/PathsManagement';
import EventsManagement from './pages/EventsManagement';
import SubmissionFormsManagement from './pages/SubmissionFormsManagement';
import QuestionsManagement from './pages/QuestionsManagement';
import ReinforcementsManagement from './pages/ReinforcementsManagement';
import TagsManagement from './pages/TagsManagement';
import OnboardingPage from './components/OnboardingPage';
import PathEditor from './pages/PathEditor';
import LessonEditor from './pages/LessonEditor';
import CourseEditor from './pages/CourseEditor';
import SubmissionEditor from './pages/SubmissionEditor';
import EventEditor from './pages/EventEditor';
import SubmissionFormEditor from './pages/SubmissionFormEditor';
import QuestionEditor from './pages/QuestionEditor';
import SurveyPage from './pages/SurveyPage';
import ReinforcementEditor from './pages/ReinforcementEditor';
import TagEditor from './pages/TagEditor';
import BadgesManagement from './pages/BadgesManagement';
import BadgeEditor from './pages/BadgeEditor';
import TestSubmissionAPI from './components/TestSubmissionAPI';
import StudentSubmissionForms from './pages/StudentSubmissionForms';
import LessonPage from './pages/LessonPage';
import ContactHelp from './pages/ContactHelp';
import BadgeEarnedPopup from './components/badges/BadgeEarnedPopup';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Leaderboard from './pages/Leaderboard';
import CongratulationsPreview from './components/CongratulationsPreview';
import Footer from './components/Footer';
import PlatformData from './pages/PlatformData';
import ClaimSkillsBuildPage from './pages/ClaimSkillsBuildPage';

// Applies per-program primary color globally for students
const ProgramColorOverride = ({ user }) => {
  useEffect(() => {
    const programColor = user?.group?.program?.primaryColor;
    if (programColor && programColor.match(/^#[0-9a-fA-F]{6}$/)) {
      const r = parseInt(programColor.slice(1, 3), 16) / 255;
      const g = parseInt(programColor.slice(3, 5), 16) / 255;
      const b = parseInt(programColor.slice(5, 7), 16) / 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: h = 0;
        }
      }
      const hsl = `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
      document.documentElement.style.setProperty('--primary', hsl);
    } else {
      document.documentElement.style.removeProperty('--primary');
    }
    return () => {
      document.documentElement.style.removeProperty('--primary');
    };
  }, [user?.group?.program?.primaryColor]);
  return null;
};

// AdminRoute wrapper as a function component
const AdminRoute = ({ isAuthenticated, user, children }) => {
  if (!isAuthenticated || !user || (user.role !== 'admin' && user.role !== 'platform_admin')) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// InstructorRoute wrapper as a function component (allows both instructors and admins)
const InstructorRoute = ({ isAuthenticated, user, children }) => {
  if (!isAuthenticated || !user || (user.role !== 'instructor' && user.role !== 'admin' && user.role !== 'platform_admin')) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// Protected route that checks onboarding status
const ProtectedRoute = ({ isAuthenticated, user, onboardingResult, children }) => {
  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }

  // Only students need onboarding
  // Allow access if they have completed onboarding OR if they have onboarding results (just completed)
  if (user.role === 'student' && !user.hasCompletedOnboarding && !onboardingResult && user.group) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

class App extends Component {
  componentDidMount() {
    this.props.loadUser();
  }

  render() {
    // Get auth state from Redux props
    const { isAuthenticated, user, isLoading } = this.props.auth;
    const { result: onboardingResult } = this.props.onboarding || {};

    // Show loading spinner during initial authentication check
    if (isLoading && !user) {
      return <Spinner />;
    }

    return (
      <Router>
        <div className={`min-h-screen flex flex-col ${isAuthenticated ? 'bg-gradient-to-b from-white to-[#f9fafb] dark:from-[hsl(var(--background))] dark:to-[hsl(var(--background))]' : 'force-light bg-white'}`}>
          <AppNavbar />
          <ProgramColorOverride user={user} />
          <BadgeEarnedPopup />
          <div className="flex-1 flex flex-col">
          <Routes>
            <Route
              path="/contact-help"
              element={<ContactHelp />}
            />
            <Route
              path="/login"
              element={isAuthenticated ? <Navigate to="/" replace /> : <div className="force-light flex-1 bg-white"><LoginPage /></div>}
            />
            <Route
              path="/register"
              element={<div className="force-light flex-1 bg-white"><RegisterPage /></div>}
            />
            <Route path="*" element={
              <div className="w-full max-w-none 2xl:max-w-[90vw] wide-screen-container mx-auto px-0 md:px-4 sm:px-6 lg:px-8 lg:pb-0 pb-0 flex-1 flex flex-col">
                <Routes>
                  <Route exact path="/" element={<div className="force-light flex-1 bg-white"><Home /></div>} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route
                    path="/claim-skillsbuild"
                    element={<ClaimSkillsBuildPage />}
                  />
                  <Route
                    path="/onboarding"
                    element={<OnboardingPage />}
                  />
                  <Route
                    path="/congratulations-preview"
                    element={<CongratulationsPreview />}
                  />
                  <Route
                    path="/content-management/lessons"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <LessonsManagement />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/lessons/new"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <LessonEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/lessons/:id/edit"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <LessonEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/lessons/:id/view"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <LessonEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/events"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <EventsManagement />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/events/new"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <EventEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/events/:id/edit"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <EventEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/events/:id/view"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <EventEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/courses"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <CoursesManagement />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/courses/new"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <CourseEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/courses/:id/edit"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <CourseEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/courses/:id/view"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <CourseEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/submissions"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <SubmissionsManagement />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/submissions/:id/view"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <SubmissionEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/submissions/:id/edit"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <SubmissionEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/submission-forms"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <SubmissionFormsManagement />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/submission-forms/new"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <SubmissionFormEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/submission-forms/:id/edit"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <SubmissionFormEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/submission-forms/:id/view"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <SubmissionFormEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/paths"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <PathsManagement />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/paths/new"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <PathEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/paths/:id/edit"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <PathEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/paths/:id/view"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <PathEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/questions"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <QuestionsManagement />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/questions/new"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <QuestionEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/questions/:id/edit"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <QuestionEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/questions/:id/view"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <QuestionEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/reinforcements"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <ReinforcementsManagement />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/reinforcements/new"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <ReinforcementEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/reinforcements/:id/edit"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <ReinforcementEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/reinforcements/:id/view"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <ReinforcementEditor />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/content-management/tags"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <TagsManagement />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/tags/new"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <TagEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/tags/:id/edit"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <TagEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/tags/:id/view"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <TagEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/badges"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <BadgesManagement />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/badges/new"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <BadgeEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/badges/:id/edit"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <BadgeEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management/badges/:id/view"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <BadgeEditor />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/content-management"
                    element={
                      <InstructorRoute isAuthenticated={isAuthenticated} user={user}>
                        <ContentManagement />
                      </InstructorRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <Settings />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/platform-data"
                    element={
                      <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                        <PlatformData />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute isAuthenticated={isAuthenticated} user={user} onboardingResult={onboardingResult}>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute isAuthenticated={isAuthenticated} user={user} onboardingResult={onboardingResult}>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/messages"
                    element={
                      <ProtectedRoute isAuthenticated={isAuthenticated} user={user} onboardingResult={onboardingResult}>
                        <Messages />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/survey/:programId/:kind"
                    element={
                      <ProtectedRoute isAuthenticated={isAuthenticated} user={user} onboardingResult={onboardingResult}>
                        <SurveyPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/lesson/:slug"
                    element={
                      <ProtectedRoute isAuthenticated={isAuthenticated} user={user} onboardingResult={onboardingResult}>
                        <LessonPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/submissions"
                    element={
                      <ProtectedRoute isAuthenticated={isAuthenticated} user={user} onboardingResult={onboardingResult}>
                        <StudentSubmissionForms />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/submissions/:formId"
                    element={
                      <ProtectedRoute isAuthenticated={isAuthenticated} user={user} onboardingResult={onboardingResult}>
                        <StudentSubmissionForms />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/test-submissions"
                    element={<TestSubmissionAPI />}
                  />
                </Routes>
              </div>
            } />
          </Routes>
          </div>
          <Footer />
          {isAuthenticated && user?.role === 'student' && <ChatBot />}
        </div>
      </Router>
    );
  }
}

const mapStateToProps = state => ({
  auth: state.auth,
  onboarding: state.onboarding
});

export default connect(mapStateToProps, { loadUser, checkOnboardingStatus })(App);
